import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './MultiSelectList.module.scss';
import Label from '../../label/Label';
import Checkbox from '../../checkbox/Checkbox';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import { useBrandingContext } from 'app-context/BrandingsContext';
import Tooltip from 'component/Custom/tooltip/Tooltip';

const toArraySafe = (v) => (Array.isArray(v) ? v : []);

const MultiSelectList = ({
  label,
  placeholder,
  name,

  value, // controlled
  defaultValue = [], // uncontrolled initial

  onChange,
  style,
  type,
  error,
  touched,
  options,
  config = { hasClear: true, hasPlusBtn: true },
  clearText,
  isLoading,
  isFolder,
  isAll,
  disabled,
  showTags = true,
  cursorNotAllowed = false,
  showTagClose = true,
}) => {
  const isControlled = value !== undefined;

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedValues, setSelectedValues] = useState(() =>
    isControlled ? toArraySafe(value) : toArraySafe(defaultValue),
  );

  const [dropdownStyle, setDropdownStyle] = useState({});
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [isError, setIsError] = useState(false);
  const [visibleItemsCount, setVisibleItemsCount] = useState(20);
  const [dropdownDirection, setDropdownDirection] = useState('bottom');

  const { brandingData } = useBrandingContext();
  const { page_TextColor } = brandingData;

  const inputCursorStyle = cursorNotAllowed ? { cursor: 'not-allowed' } : style;

  const handleSelect = useCallback(
    (newValue, newType) => {
      let updatedValues;
      const selectedTypes = selectedValues.map((val) => {
        const option = options.find((opt) => opt.value === val);
        return option ? option.type : null;
      });

      if (newValue === 'all') {
        updatedValues = ['all'];
      } else {
        // this code is for choosing only files or folder  (don't delete it)
        // const hasType1OrTrue =
        //   selectedTypes.includes(1) || selectedTypes.includes(true);
        // const hasTypeFalse = selectedTypes.includes(false);
        // if (
        //   (newType === false && hasType1OrTrue) ||
        //   ((newType === 1 || newType === true) && hasTypeFalse)
        // ) {
        //   setIsError(true);
        //   return;
        // }

        updatedValues = selectedValues.includes('all')
          ? [newValue]
          : selectedValues.includes(newValue)
            ? selectedValues.filter((val) => val !== newValue)
            : [...selectedValues.filter((val) => val !== 'all'), newValue];
      }

      setSelectedValues(updatedValues);

      onChange?.({
        target: {
          name,
          value: updatedValues,
        },
      });
    },
    [name, onChange, selectedValues, options],
  );

  const handleClear = () => {
    setSelectedValues([]);
    onChange?.({
      target: {
        name,
        value: [],
      },
    });
  };

  const stopOutside = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.nativeEvent?.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  }, []);

  const filteredOptions = useMemo(() => {
    const safe = Array.isArray(options) ? options : [];

    const byValue = new Map();
    for (const opt of safe) {
      if (!opt || !opt.value) continue;
      if (!byValue.has(opt.value)) byValue.set(opt.value, opt);
    }
    const deduped = Array.from(byValue.values());

    const term = (searchTerm || '').toLowerCase();
    const list = deduped
      .filter((opt) => (opt.label || '').toLowerCase().includes(term))
      .map((opt) => ({
        value: opt.value,
        label: opt.label,
        checked: selectedValues.includes(opt.value),
        type: isFolder ? opt.type : false,
      }));

    return list.slice(0, visibleItemsCount);
  }, [options, searchTerm, selectedValues, visibleItemsCount, isFolder]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const { top, bottom, left, width } =
        inputRef.current.getBoundingClientRect();

      const dropdownEl = dropdownRef.current;
      const dropdownHeight = dropdownEl?.offsetHeight || 200;
      const spaceBelow = window.innerHeight - bottom;
      const shouldOpenUp = spaceBelow < dropdownHeight;

      setDropdownDirection(shouldOpenUp ? 'top' : 'bottom');

      const topPosition = shouldOpenUp
        ? top - dropdownHeight + window.scrollY
        : bottom + window.scrollY;

      setDropdownStyle({
        position: 'absolute',
        width: `${width}px`,
        minWidth: '100px',
        top: `${topPosition}px`,
        left: `${left + window.scrollX}px`,
        color: page_TextColor,
        zIndex: 9999,
      });
    }
  }, [isOpen, filteredOptions, page_TextColor]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScrollOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScrollOutside, true);
      window.addEventListener('resize', handleResize);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOutside, true);
      window.removeEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOutside, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  // ✅ only sync from prop when CONTROLLED
  useEffect(() => {
    if (!isControlled) return;

    const next = toArraySafe(value);
    if (JSON.stringify(next) !== JSON.stringify(selectedValues)) {
      setSelectedValues(next);
    }
  }, [isControlled, value, selectedValues]);

  const handleDropdownScroll = useCallback(() => {
    if (
      dropdownRef.current &&
      dropdownRef.current.scrollTop + dropdownRef.current.clientHeight >=
        dropdownRef.current.scrollHeight - 20
    ) {
      setVisibleItemsCount((prevCount) => prevCount + 50);
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setIsError(false);
    }, [3000]);
  }, [isError]);

  return (
    <div className={styles['select-container']} style={style}>
      {label && <Label text={label} htmlFor={`for-${name}`} />}

      <div
        className={styles['select-flex']}
        tabIndex={0}
        aria-controls={`select-dropdown-${name}`}
        aria-expanded={isOpen}
      >
        <div
          className={styles['select-wrapper']}
          style={inputCursorStyle}
          onClick={isLoading || disabled ? () => {} : () => setIsOpen(!isOpen)}
        >
          <div className={styles['select-display']}>
            <input
              id={`select-${name}`}
              type='text'
              ref={inputRef}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target?.value);
                if (!isOpen) setIsOpen(true);
              }}
              autoComplete='off'
              placeholder={placeholder}
              className={`${styles['select']} ${
                !searchTerm && !selectedValues?.length
                  ? styles['placeholder']
                  : ''
              }`}
              disabled={disabled}
              style={inputCursorStyle}
            />

            {config.hasPlusBtn ? (
              isLoading ? (
                <div className={styles['loader-container']} />
              ) : (
                <SvgWithBranding type='select' className={styles.icon} />
              )
            ) : null}
          </div>

          {isOpen &&
            createPortal(
              <div
                className={`${styles['options']} ${styles['dropdown-animation']}`}
                style={dropdownStyle}
                ref={dropdownRef}
                onScroll={handleDropdownScroll}
                onMouseDown={stopOutside}
                onClick={stopOutside}
              >
                {isAll && (
                  <div
                    className={styles['option']}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (e.nativeEvent?.stopImmediatePropagation)
                        e.nativeEvent.stopImmediatePropagation();

                      setSelectedValues([
                        '00000000-0000-0000-0000-000000000000',
                      ]);
                      onChange?.({ target: { name, value: ['all'] } });

                      setIsOpen(true);
                      inputRef.current?.focus({ preventScroll: true });
                    }}
                  >
                    <div className={styles['folder-container']}>
                      <p>All</p>
                    </div>
                  </div>
                )}

                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => {
                    if (option?.value == 'all') return null;

                    return (
                      <div
                        key={option.value}
                        className={`${styles['option']} ${
                          option.checked ? styles['selected'] : ''
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (e.nativeEvent?.stopImmediatePropagation)
                            e.nativeEvent.stopImmediatePropagation();

                          handleSelect(option.value, option.type);
                          setIsOpen(true);
                          inputRef.current?.focus({ preventScroll: true });
                        }}
                      >
                        {isFolder ? (
                          <div className={styles['folder-container']}>
                            <SvgWithBranding
                              type={
                                option.type === 1
                                  ? 'url'
                                  : option.type === true
                                    ? 'folder'
                                    : 'table-file'
                              }
                              className={styles['icon']}
                            />
                            <p>{option.label}</p>
                          </div>
                        ) : (
                          <Checkbox
                            label={option.label}
                            ischecked={option.checked}
                            onChange={() => handleSelect(option.value)}
                          />
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className={styles['no-options']}>No data found</div>
                )}
              </div>,
              document.body,
            )}
        </div>

        {config.hasClear && clearText && (
          <p onClick={handleClear} className={styles.clear}>
            {clearText}
          </p>
        )}
      </div>

      {showTags && selectedValues?.length > 0 && (
        <div className={styles['selected-items-container']}>
          {selectedValues?.map((valueItem, index) => {
            const option = options?.find((opt) => opt.value === valueItem);
            return (
              option && (
                <Tooltip key={index} content={option.label} placement='top'>
                  <div key={option.value} className={styles['selected-item']}>
                    {isFolder && (
                      <SvgWithBranding
                        type={
                          option.label == 'All'
                            ? 'folder'
                            : option.type === 1
                              ? 'url'
                              : option.type === true
                                ? 'folder'
                                : 'table-file'
                        }
                        className={styles['icon']}
                      />
                    )}

                    <p> {option.label}</p>
                    {showTagClose && (
                      <img
                        src='/images/close-grey.svg'
                        onClick={() => handleSelect(option.value)}
                      />
                    )}
                  </div>
                </Tooltip>
              )
            );
          })}
        </div>
      )}

      {error && touched && <div className={'error-message'}>{error}</div>}
      {isError && (
        <p className='error-message'>
          You can't choose folders and files together
        </p>
      )}
    </div>
  );
};

export default MultiSelectList;
